import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, MapPin, Pencil, X } from 'lucide-react';

import MapPickerModal from 'features/onboarding/components/forms/MapPickerModal';
import type { PlaceDetails } from 'features/onboarding/services/geocoding.service';
import { cn } from 'shared/lib/utils';

interface GeolocationCaptureProps {
  lat?: number | null;
  lng?: number | null;
  address?: string;
  /**
   * The address resolved from the pin, shown read-only as the primary line of
   * the captured state. The partner never types this — it comes back from the
   * Google proxy when the pin is confirmed.
   */
  resolvedAddress?: string;
  /**
   * `details` is provided when the coordinates resolved to a Google place, so
   * callers can auto-fill the structured address fields. `null` when the
   * lookup failed or returned nothing usable.
   */
  onCapture: (lat: number, lng: number, details?: PlaceDetails | null) => void;
  onClear: () => void;
  /** Validation error code (e.g. from RHF); rendered translated when set. */
  error?: string;
}

const GeolocationCapture: React.FC<GeolocationCaptureProps> = ({
  lat,
  lng,
  address,
  resolvedAddress,
  onCapture,
  onClear,
  error,
}) => {
  const { t } = useTranslation();
  const captured =
    typeof lat === 'number' && typeof lng === 'number' && !Number.isNaN(lat) && !Number.isNaN(lng);
  const [mapOpen, setMapOpen] = useState(false);

  const handleMapConfirm = (
    latVal: number,
    lngVal: number,
    details: PlaceDetails | null,
  ) => {
    onCapture(latVal, lngVal, details);
  };

  const coordinatesLabel = captured
    ? (t('businessInfo.geolocation.captured', {
        lat: lat!.toFixed(5),
        lng: lng!.toFixed(5),
      }) as string)
    : '';

  return (
    <>
      <div
        data-error={error ? 'true' : undefined}
        className={cn(
          'rounded-2xl border p-4 flex flex-col gap-3',
          captured
            ? 'border-emerald-200 bg-emerald-50/40'
            : error
              ? 'border-red-300 bg-red-50/40'
              : 'border-gray-200 bg-gray-50',
        )}
      >
        <div className="flex items-start gap-3">
          <span
            className={cn(
              'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
              captured ? 'bg-white text-emerald-600' : 'bg-white text-gray-600',
            )}
          >
            <MapPin size={18} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold text-gray-800">
              {t('businessInfo.geolocation.title')}
            </p>
            <p className="text-sm text-gray-600 mt-0.5">
              {t('businessInfo.geolocation.subtitle')}
            </p>
          </div>
        </div>

        {captured ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white border border-emerald-200 px-3 py-2">
            <span className="inline-flex items-start gap-2 min-w-0">
              <CheckCircle2
                size={16}
                className="flex-shrink-0 mt-0.5 text-emerald-600"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-emerald-800 break-words">
                  {resolvedAddress?.trim() || coordinatesLabel}
                </span>
                {resolvedAddress?.trim() ? (
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {coordinatesLabel}
                  </span>
                ) : null}
              </span>
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setMapOpen(true)}
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-800"
              >
                <Pencil size={14} />
                {t('businessInfo.geolocation.editOnMap')}
              </button>
              <button
                type="button"
                onClick={onClear}
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-red-600"
              >
                <X size={14} />
                {t('businessInfo.geolocation.clear')}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setMapOpen(true)}
            className={cn(
              'inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors',
              'bg-haat-red-600 text-white hover:bg-haat-red-700',
            )}
          >
            <MapPin size={16} />
            {t('businessInfo.geolocation.pickOnMap')}
          </button>
        )}

        {!captured &&
          (error ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600">
              <AlertTriangle size={12} />
              {t(`validation.${error}`, { defaultValue: error })}
            </span>
          ) : (
            <span className="text-xs text-gray-500">
              {t('businessInfo.geolocation.optionalHint')}
            </span>
          ))}
      </div>

      <MapPickerModal
        open={mapOpen}
        initialLat={lat ?? null}
        initialLng={lng ?? null}
        initialAddress={address}
        onConfirm={handleMapConfirm}
        onClose={() => setMapOpen(false)}
      />
    </>
  );
};

export default GeolocationCapture;
